import React, { useState } from 'react';
import { X, Info } from 'lucide-react';
import { useTutorial } from '../../contexts/TutorialContext';

/**
 * Tooltip Component - แสดงคำแนะนำเมื่อ hover
 * @param {Object} props
 * @param {React.ReactNode} props.children - Element ที่จะแสดง tooltip
 * @param {string} props.content - เนื้อหาของ tooltip
 * @param {string} props.title - หัวข้อของ tooltip (optional)
 * @param {string} props.position - ตำแหน่งของ tooltip ('top', 'bottom', 'left', 'right')
 * @param {boolean} props.showIcon - แสดงไอคอน info (optional)
 * @param {boolean} props.persistent - แสดง tooltip ตลอดเวลา (ไม่ต้อง hover)
 */
const Tooltip = ({ 
  children, 
  content, 
  title,
  position = 'top',
  showIcon = false,
  persistent = false,
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(persistent);
  const { showTooltips } = useTutorial();
  const [dismissed, setDismissed] = useState(false);

  if (!showTooltips || dismissed || !content) {
    return children;
  }

  const handleDismiss = (e) => {
    e.stopPropagation();
    setDismissed(true);
  };

  const positionClasses = {
    top: 'bottom-full left-1/2 transform -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 transform -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 transform -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 transform -translate-y-1/2 ml-2',
  };

  const arrowClasses = {
    top: 'top-full left-1/2 transform -translate-x-1/2 border-t-gray-900 border-l-transparent border-r-transparent border-b-transparent',
    bottom: 'bottom-full left-1/2 transform -translate-x-1/2 border-b-gray-900 border-l-transparent border-r-transparent border-t-transparent',
    left: 'left-full top-1/2 transform -translate-y-1/2 border-l-gray-900 border-t-transparent border-b-transparent border-r-transparent',
    right: 'right-full top-1/2 transform -translate-y-1/2 border-r-gray-900 border-t-transparent border-b-transparent border-l-transparent',
  };

  return (
    <div 
      className={`relative inline-block ${className}`}
      onMouseEnter={() => !persistent && setIsVisible(true)}
      onMouseLeave={() => !persistent && setIsVisible(false)}
    >
      {children}
      
      {showIcon && !dismissed && (
        <div className="absolute -top-1 -right-1 z-10">
          <div className="h-5 w-5 bg-blue-500 rounded-full flex items-center justify-center text-white shadow-lg animate-pulse">
            <Info className="h-3 w-3" />
          </div>
        </div>
      )}

      {isVisible && (
        <div 
          className={`absolute z-50 ${positionClasses[position]} animate-fadeIn`}
          style={{ minWidth: '200px', maxWidth: '300px' }}
        >
          <div className="bg-gray-900 dark:bg-gray-800 text-white rounded-lg shadow-xl p-3 relative">
            {/* Arrow */}
            <div 
              className={`absolute border-8 ${arrowClasses[position]}`}
            />
            
            {/* Content */}
            <div className="relative">
              {persistent && (
                <button
                  onClick={handleDismiss}
                  className="absolute -top-1 -right-1 text-gray-400 hover:text-white transition-colors"
                  aria-label="ปิด"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
              
              {title && (
                <div className="font-semibold text-sm mb-1 pr-4">
                  {title}
                </div>
              )}
              
              <div className="text-xs text-gray-200 dark:text-gray-300">
                {content}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tooltip;

